import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FiCheckCircle,
  FiClock,
  FiDollarSign,
  FiPlus,
  FiUsers,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";

import {
  config,
} from "../../../../../../utils/apiUrl";

import EmployeePageHeader
  from "../common/EmployeePageHeader";

import EmployeeModuleTabs
  from "../common/EmployeeModuleTabs";

import EmployeeSelector
  from "../common/EmployeeSelector";

import EmployeeSalaryTable
  from "./EmployeeSalaryTable";

import GenerateSalaryModal
  from "./GenerateSalaryModal";

import SalaryPaymentModal
  from "./SalaryPaymentModal";

import SalaryDetailsDrawer
  from "./SalaryDetailsDrawer";

import {
  formatEmployeeCurrency,
  getEmployeeApiError,
} from "../utils/employee.utils";

import "./EmployeeSalary.css";


const getCurrentMonth =
  () =>
    new Date()
      .getMonth() +
    1;


const getCurrentYear =
  () =>
    new Date()
      .getFullYear();


const INITIAL_FILTERS = {
  employeeId: "",
  month: getCurrentMonth(),
  year: getCurrentYear(),
  status: "",
  search: "",
};


const INITIAL_PAGINATION = {
  page: 1,
  limit: 10,
  totalRecords: 0,
  totalPages: 1,
};


const INITIAL_SUMMARY = {
  totalEmployees: 0,
  totalSalary: 0,
  paidAmount: 0,
  balanceAmount: 0,
  paidCount: 0,
  pendingCount: 0,
};


const EmployeeSalary = () => {

  const api =
    useMemo(
      () =>
        new API(),
      []
    );


  const [
    records,
    setRecords,
  ] =
    useState([]);


  const [
    employees,
    setEmployees,
  ] =
    useState([]);


  const [
    summary,
    setSummary,
  ] =
    useState({
      ...INITIAL_SUMMARY,
    });


  const [
    filters,
    setFilters,
  ] =
    useState({
      ...INITIAL_FILTERS,
    });


  const [
    appliedFilters,
    setAppliedFilters,
  ] =
    useState({
      ...INITIAL_FILTERS,
    });


  const [
    pagination,
    setPagination,
  ] =
    useState({
      ...INITIAL_PAGINATION,
    });


  const [
    loading,
    setLoading,
  ] =
    useState(false);


  const [
    employeeLoading,
    setEmployeeLoading,
  ] =
    useState(false);


  const [
    error,
    setError,
  ] =
    useState("");


  const [
    selectedSalary,
    setSelectedSalary,
  ] =
    useState(null);


  const [
    generateOpen,
    setGenerateOpen,
  ] =
    useState(false);


  const [
    paymentOpen,
    setPaymentOpen,
  ] =
    useState(false);


  const [
    detailsOpen,
    setDetailsOpen,
  ] =
    useState(false);


  /*
  |--------------------------------------------------------------------------
  | LOAD EMPLOYEES
  |--------------------------------------------------------------------------
  */

  const loadEmployees =
    useCallback(
      async () => {

        try {

          setEmployeeLoading(
            true
          );


          const response =
            await api.post(
              config.getEmployees,
              {
                page: 1,
                limit: 500,
                status: "ACTIVE",
              }
            );


          if (
            response?.status !==
            "success"
          ) {

            throw new Error(
              response?.message ||
              "Unable to load employees."
            );

          }


          setEmployees(
            Array.isArray(
              response?.data
            )
              ?
              response.data
              :
              []
          );

        } catch (
          requestError
        ) {

          console.error(
            "Load salary employees error:",
            requestError
          );


          setEmployees(
            []
          );

        } finally {

          setEmployeeLoading(
            false
          );

        }

      },
      [
        api,
      ]
    );


  /*
  |--------------------------------------------------------------------------
  | LOAD SALARY
  |--------------------------------------------------------------------------
  */

  const loadSalary =
    useCallback(
      async () => {

        try {

          setLoading(
            true
          );


          setError(
            ""
          );


          const response =
            await api.post(
              config.getEmployeeSalaries,
              {
                page:
                  pagination.page,

                limit:
                  pagination.limit,

                employeeId:
                  appliedFilters.employeeId,

                month:
                  Number(
                    appliedFilters.month
                  ),

                year:
                  Number(
                    appliedFilters.year
                  ),

                status:
                  appliedFilters.status,

                search:
                  appliedFilters.search,
              }
            );


          if (
            response?.status !==
            "success"
          ) {

            throw new Error(
              response?.message ||
              "Unable to load employee salaries."
            );

          }


          const data =
            Array.isArray(
              response?.data
            )
              ?
              response.data
              :
              [];


          setRecords(
            data
          );


          setPagination(
            (
              previous
            ) => ({
              ...previous,

              totalRecords:
                Number(
                  response
                    ?.pagination
                    ?.totalRecords ??
                  data.length
                ),

              totalPages:
                Math.max(
                  Number(
                    response
                      ?.pagination
                      ?.totalPages
                  ) ||
                  1,
                  1
                ),
            })
          );

        } catch (
          requestError
        ) {

          console.error(
            "Load employee salaries error:",
            requestError
          );


          setRecords(
            []
          );


          setError(
            getEmployeeApiError(
              requestError,
              "Unable to load employee salaries."
            )
          );

        } finally {

          setLoading(
            false
          );

        }

      },
      [
        api,
        appliedFilters,
        pagination.page,
        pagination.limit,
      ]
    );


  /*
  |--------------------------------------------------------------------------
  | LOAD SUMMARY
  |--------------------------------------------------------------------------
  */

  const loadSummary =
    useCallback(
      async () => {

        try {

          const response =
            await api.get(
              config.getEmployeeSalarySummary,
              {
                employeeId:
                  appliedFilters.employeeId,

                month:
                  Number(
                    appliedFilters.month
                  ),

                year:
                  Number(
                    appliedFilters.year
                  ),
              }
            );


          if (
            response?.status !==
            "success"
          ) {

            return;

          }


          const data =
            response?.data ||
            {};


          setSummary({
            totalEmployees:
              Number(
                data.totalEmployees ??
                data.employeeCount ??
                0
              ),

            totalSalary:
              Number(
                data.totalSalary ??
                data.netSalary ??
                data.totalNetSalary ??
                0
              ),

            paidAmount:
              Number(
                data.paidAmount ??
                data.totalPaidAmount ??
                0
              ),

            balanceAmount:
              Number(
                data.balanceAmount ??
                data.totalBalanceAmount ??
                0
              ),

            paidCount:
              Number(
                data.paidCount ??
                0
              ),

            pendingCount:
              Number(
                data.pendingCount ??
                data.unpaidCount ??
                0
              ),
          });

        } catch (
          requestError
        ) {

          console.error(
            "Salary summary error:",
            requestError
          );

        }

      },
      [
        api,
        appliedFilters.employeeId,
        appliedFilters.month,
        appliedFilters.year,
      ]
    );


  useEffect(
    () => {

      loadEmployees();

    },
    [
      loadEmployees,
    ]
  );


  useEffect(
    () => {

      loadSalary();
      loadSummary();

    },
    [
      loadSalary,
      loadSummary,
    ]
  );


  /*
  |--------------------------------------------------------------------------
  | FILTER
  |--------------------------------------------------------------------------
  */

  const handleSearch =
    () => {

      setPagination(
        (
          previous
        ) => ({
          ...previous,
          page: 1,
        })
      );


      setAppliedFilters({
        ...filters,

        search:
          String(
            filters.search ||
            ""
          ).trim(),
      });

    };


  const handleReset =
    () => {

      const reset = {
        ...INITIAL_FILTERS,
      };


      setFilters(
        reset
      );


      setAppliedFilters(
        reset
      );


      setPagination(
        (
          previous
        ) => ({
          ...previous,
          page: 1,
        })
      );

    };


  /*
  |--------------------------------------------------------------------------
  | SUCCESS
  |--------------------------------------------------------------------------
  */

  const handleSuccess =
    async () => {

      setGenerateOpen(
        false
      );


      setPaymentOpen(
        false
      );


      setDetailsOpen(
        false
      );


      setSelectedSalary(
        null
      );


      await Promise.all([
        loadSalary(),
        loadSummary(),
      ]);

    };


  const summaryCards = [

    {
      label:
        "Employees",

      value:
        summary.totalEmployees,

      icon:
        FiUsers,

      type:
        "blue",
    },

    {
      label:
        "Total Salary",

      value:
        formatEmployeeCurrency(
          summary.totalSalary
        ),

      icon:
        FiDollarSign,

      type:
        "purple",
    },

    {
      label:
        "Paid Amount",

      value:
        formatEmployeeCurrency(
          summary.paidAmount
        ),

      icon:
        FiCheckCircle,

      type:
        "green",
    },

    {
      label:
        "Balance",

      value:
        formatEmployeeCurrency(
          summary.balanceAmount
        ),

      icon:
        FiClock,

      type:
        "orange",
    },

  ];


  return (

    <div className="employee-salary-page">

      <EmployeePageHeader
        title="Salary & Payroll"
        description="Generate monthly employee salary, record payments and monitor outstanding payroll."
        actionLabel="Generate Salary"
        actionIcon={
          <FiPlus />
        }
        onAction={
          () =>
            setGenerateOpen(
              true
            )
        }
        onRefresh={
          () =>
            Promise.all([
              loadSalary(),
              loadSummary(),
            ])
        }
        loading={
          loading
        }
      />


      <EmployeeModuleTabs />


      <div className="employee-salary-summary-grid">

        {
          summaryCards.map(
            (
              card
            ) => {

              const Icon =
                card.icon;


              return (

                <article
                  key={
                    card.label
                  }
                  className={`employee-salary-summary-card employee-salary-summary-card--${card.type}`}
                >

                  <span className="employee-salary-summary-icon">

                    <Icon />

                  </span>


                  <div>

                    <small>
                      {card.label}
                    </small>

                    <strong>
                      {card.value}
                    </strong>

                  </div>

                </article>

              );

            }
          )
        }

      </div>


      <section className="employee-salary-filter-card">

        <div className="employee-salary-filter-grid">

          <div className="employee-salary-filter-field">

            <EmployeeSelector
              employees={
                employees
              }
              value={
                filters.employeeId
              }
              onChange={
                (
                  employeeId
                ) =>
                  setFilters(
                    (
                      previous
                    ) => ({
                      ...previous,
                      employeeId,
                    })
                  )
              }
              label="Employee"
              placeholder="All Employees"
              disabled={
                employeeLoading
              }
            />

          </div>


          <div className="employee-salary-filter-field">

            <label>
              Month
            </label>

            <select
              value={
                filters.month
              }
              onChange={
                (
                  event
                ) =>
                  setFilters(
                    (
                      previous
                    ) => ({
                      ...previous,

                      month:
                        Number(
                          event
                            .target
                            .value
                        ),
                    })
                  )
              }
            >

              {
                [
                  "January",
                  "February",
                  "March",
                  "April",
                  "May",
                  "June",
                  "July",
                  "August",
                  "September",
                  "October",
                  "November",
                  "December",
                ]
                  .map(
                    (
                      month,
                      index
                    ) => (

                      <option
                        key={
                          month
                        }
                        value={
                          index +
                          1
                        }
                      >
                        {month}
                      </option>

                    )
                  )
              }

            </select>

          </div>


          <div className="employee-salary-filter-field">

            <label>
              Year
            </label>

            <select
              value={
                filters.year
              }
              onChange={
                (
                  event
                ) =>
                  setFilters(
                    (
                      previous
                    ) => ({
                      ...previous,

                      year:
                        Number(
                          event
                            .target
                            .value
                        ),
                    })
                  )
              }
            >

              {
                Array.from(
                  {
                    length: 7,
                  },
                  (
                    _,
                    index
                  ) =>
                    getCurrentYear() -
                    3 +
                    index
                )
                  .map(
                    (
                      year
                    ) => (

                      <option
                        key={
                          year
                        }
                        value={
                          year
                        }
                      >
                        {year}
                      </option>

                    )
                  )
              }

            </select>

          </div>


          <div className="employee-salary-filter-field">

            <label>
              Payment Status
            </label>

            <select
              value={
                filters.status
              }
              onChange={
                (
                  event
                ) =>
                  setFilters(
                    (
                      previous
                    ) => ({
                      ...previous,

                      status:
                        event
                          .target
                          .value,
                    })
                  )
              }
            >

              <option value="">
                All Status
              </option>

              <option value="UNPAID">
                Unpaid
              </option>

              <option value="PARTIALLY_PAID">
                Partially Paid
              </option>

              <option value="PAID">
                Paid
              </option>

              <option value="CANCELLED">
                Cancelled
              </option>

            </select>

          </div>


          <div className="employee-salary-filter-field employee-salary-search-field">

            <label>
              Search
            </label>

            <input
              type="text"
              value={
                filters.search
              }
              onChange={
                (
                  event
                ) =>
                  setFilters(
                    (
                      previous
                    ) => ({
                      ...previous,

                      search:
                        event
                          .target
                          .value,
                    })
                  )
              }
              onKeyDown={
                (
                  event
                ) => {

                  if (
                    event.key ===
                    "Enter"
                  ) {

                    handleSearch();

                  }

                }
              }
              placeholder="Name or employee code"
            />

          </div>


          <div className="employee-salary-filter-actions">

            <button
              type="button"
              className="employee-salary-search-btn"
              onClick={
                handleSearch
              }
            >
              Apply
            </button>


            <button
              type="button"
              className="employee-salary-reset-btn"
              onClick={
                handleReset
              }
            >
              Reset
            </button>

          </div>

        </div>

      </section>


      {
        error
          ?
          (
            <div className="employee-salary-error">
              {error}
            </div>
          )
          :
          null
      }


      <EmployeeSalaryTable
        records={
          records
        }
        loading={
          loading
        }
        page={
          pagination.page
        }
        limit={
          pagination.limit
        }
        totalRecords={
          pagination.totalRecords
        }
        totalPages={
          pagination.totalPages
        }
        onPageChange={
          (
            page
          ) =>
            setPagination(
              (
                previous
              ) => ({
                ...previous,
                page,
              })
            )
        }
        onLimitChange={
          (
            limit
          ) =>
            setPagination(
              (
                previous
              ) => ({
                ...previous,
                page: 1,
                limit,
              })
            )
        }
        onView={
          (
            salary
          ) => {

            setSelectedSalary(
              salary
            );

            setDetailsOpen(
              true
            );

          }
        }
        onPay={
          (
            salary
          ) => {

            setSelectedSalary(
              salary
            );

            setPaymentOpen(
              true
            );

          }
        }
      />


      <GenerateSalaryModal
        open={
          generateOpen
        }
        employees={
          employees
        }
        defaultMonth={
          filters.month
        }
        defaultYear={
          filters.year
        }
        onClose={
          () =>
            setGenerateOpen(
              false
            )
        }
        onSuccess={
          handleSuccess
        }
      />


      <SalaryPaymentModal
        open={
          paymentOpen
        }
        salary={
          selectedSalary
        }
        onClose={
          () => {

            setPaymentOpen(
              false
            );

            setSelectedSalary(
              null
            );

          }
        }
        onSuccess={
          handleSuccess
        }
      />


      <SalaryDetailsDrawer
        open={
          detailsOpen
        }
        salary={
          selectedSalary
        }
        onClose={
          () => {

            setDetailsOpen(
              false
            );

            setSelectedSalary(
              null
            );

          }
        }
        onPay={
          (
            salary
          ) => {

            setDetailsOpen(
              false
            );

            setSelectedSalary(
              salary
            );

            setPaymentOpen(
              true
            );

          }
        }
      />

    </div>

  );

};


export default EmployeeSalary;